import { useEffect, useRef, useState, type SyntheticEvent } from 'react'
import { rememberVideoSoundChange, setVideoSound, videoSoundPreference } from './viewport-video'

export function useViewportVideo(poster: string) {
  const video = useRef<HTMLVideoElement>(null)
  const [started, setStarted] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(true)
  const [error, setError] = useState(false)
  const [controlsVisible, setControlsVisible] = useState(false)
  const frameRequest = useRef<number | null>(null)

  useEffect(() => {
    const player = video.current
    return () => {
      if (player && frameRequest.current !== null) player.cancelVideoFrameCallback?.(frameRequest.current)
    }
  }, [])

  function resetFrame() {
    const player = video.current
    if (player && frameRequest.current !== null) player.cancelVideoFrameCallback?.(frameRequest.current)
    frameRequest.current = null
    setStarted(false)
    setPlaying(false)
  }

  function showFirstFrame() {
    const player = video.current
    if (!player) return
    setPlaying(true)
    setError(false)
    // `play` fires before a frame is available. Hide the independent poster only
    // after a presented frame, so Safari cannot expose a blank decoder surface.
    if (player.requestVideoFrameCallback) {
      if (frameRequest.current !== null) player.cancelVideoFrameCallback(frameRequest.current)
      frameRequest.current = player.requestVideoFrameCallback(() => {
        frameRequest.current = null
        if (player.readyState >= 2) setStarted(true)
      })
    } else if (player.readyState >= 2) setStarted(true)
  }

  function play() {
    const player = video.current
    if (!player) return
    setError(false)
    setVideoSound(player, videoSoundPreference.isEnabled())
    setMuted(player.muted)
    void player.play().catch(async (reason: unknown) => {
      const name = (reason as { name?: string })?.name
      if (name === 'AbortError') return
      if (name === 'NotAllowedError' && !player.muted) {
        setVideoSound(player, false)
        setMuted(true)
        try { await player.play(); return } catch (fallback) {
          if ((fallback as { name?: string })?.name === 'AbortError') return
        }
      }
      setError(true)
      resetFrame()
    })
  }

  function toggleSound() {
    const player = video.current
    if (!player) return
    const enabled = player.muted || player.volume === 0
    videoSoundPreference.setEnabled(enabled)
    setVideoSound(player, enabled)
    setMuted(!enabled)
    // This stays inside the click gesture for browsers that require activation.
    if (enabled) play()
  }

  function togglePlayback() {
    const player = video.current
    if (!player) return
    if (player.paused) play()
    else player.pause()
  }

  const videoProps = {
    ref: video, poster, muted, loop: true, playsInline: true,
    controls: started && controlsVisible, preload: 'metadata' as const,
    tabIndex: started && controlsVisible ? 0 : -1,
    'data-viewport-video': 'true', 'data-frame-ready': started ? 'true' : 'false',
    onPlaying: showFirstFrame,
    onPause: () => setPlaying(false),
    onEnded: () => setPlaying(false),
    onEmptied: resetFrame,
    onVolumeChange: (event: SyntheticEvent<HTMLVideoElement>) => setMuted(rememberVideoSoundChange(event.currentTarget)),
    onError: () => { setError(true); resetFrame() },
  }

  const previewProps = { src: poster, ready: started }
  const controlsProps = {
    muted, onToggle: toggleSound, playing, onTogglePlayback: togglePlayback,
    controlsVisible, onToggleControls: () => setControlsVisible(value => !value), ready: started,
  }
  return { started, error, videoProps, previewProps, controlsProps }
}
